import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { Input } from "@/components/ui/input";
import { FAStatusBadge, WO_STATUS_TONE } from "@/components/etams/StatusBadge";

// PilihFA — pilih FA lewat NOMOR atau NAMA CUSTOMER, lalu WO-nya DITURUNKAN.
// Lahir 2 September 2026 untuk layar Serah Terima Tahap.
//
// ⛔ SEBELUM INI layar itu meminta `wo_id` dan `fa_id` DIKETIK sebagai UUID —
//    dua kotak teks kosong. Orang gudang nol punya cara mengetahui UUID sebuah
//    pesanan, jadi seluruh mesin upah borongan di belakangnya benar dan
//    praktis nol bisa dipakai. `wo_orders` nol baris sampai hari itu; bukan
//    karena nol ada pekerjaan, melainkan karena pintunya terkunci dari dalam.
//
// Pencariannya MEMINJAM `/tracking/cari` — endpoint yang sudah ada, sudah
// dilingkupi wilayah di server, dan ketiga peran yang boleh menyerahterimakan
// (Owner, Kepala Cabang, Admin Produksi) sudah ada di daftar bacanya.
//
// WO diturunkan lewat `/wo?fa_id=` (filter baru, `contains` di
// `source_fa_ids`). Tiga keadaan, ketiganya DITAMPILKAN, nol ada yang diam:
//   0 WO  -> "FA ini belum punya WO" (tombol simpan tetap mati)
//   1 WO  -> otomatis terpilih
//   >1 WO -> dropdown; satu FA bisa dipecah ke beberapa WO
//
// Props:
//   value      : { fa, wo } | null
//   onChange   : ({ fa, wo }) => void   — wo boleh null saat belum terpilih
//   disabled
//   denganWo   : boolean, BAWAAN true — kalau false, WO nol dicari dan nol
//                dirender; `wo` selalu null.
//
// ⛔ `denganWo` DITAMBAHKAN 5 September 2026, dan sebabnya bukan kerapian.
//    Komponen ini menutup cacat UUID-diketik-tangan di Serah Terima Tahap
//    2 September — lalu berhenti di situ. Sapuan 5 September menemukan
//    EMPAT ruas lain yang masih meminta UUID mentah (`Complaints`: ID FA,
//    ID DR, ID refund; `Refunds`: ID FA), dan nol satu pun mesin bisa
//    menanyakan "apakah perbaikan ini sudah disebar?".
//
// ⚠ Memakainya apa adanya di layar itu akan MENAMBAH kebingungan, bukan
//   mengurangi: komplain dan refund nol peduli WO, tapi komponennya akan
//   memanggil `/wo?fa_id=` lalu memasang peringatan "FA ini belum punya WO"
//   pada alur yang memang nol pernah butuh WO.
//
// ⚠ Bawaannya `true` supaya `BoronganSerahTerima` — satu-satunya pemakai
//   lama — nol berubah perilakunya sama sekali.

export default function PilihFA({ value, onChange, disabled, denganWo = true,
                                 testId = "pilih-fa" }) {
  const [kata, setKata] = useState("");
  const [hasil, setHasil] = useState(null);
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState("");
  const [woList, setWoList] = useState([]);
  const [woSibuk, setWoSibuk] = useState(false);
  const kotak = useRef(null);

  const fa = value?.fa || null;
  const wo = value?.wo || null;

  // Debounce 300 ms — tiap ketukan jadi satu permintaan tanpa ini.
  useEffect(() => {
    if (fa) return;
    const q = kata.trim();
    if (q.length < 2) { setHasil(null); setGalat(""); return; }
    const t = setTimeout(async () => {
      setSibuk(true); setGalat("");
      try {
        const { data } = await api.get("/tracking/cari", { params: { q } });
        setHasil(data.items || []);
      } catch (e) {
        setHasil([]); setGalat(apiErrorMessage(e));
      } finally {
        setSibuk(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [kata, fa]);

  const pilihFa = async (f) => {
    setHasil(null); setKata("");
    onChange({ fa: f, wo: null });
    // Tanpa WO: selesai di sini. Nol permintaan jaringan yang hasilnya nol
    // pernah dibaca, dan nol peringatan tentang sesuatu yang nol dibutuhkan.
    if (!denganWo) { setWoList([]); setGalat(""); return; }
    setWoSibuk(true); setGalat("");
    try {
      const { data } = await api.get("/wo", { params: { fa_id: f.id, page_size: 20 } });
      const daftar = data.items || [];
      setWoList(daftar);
      // Satu WO -> langsung; nol atau banyak -> biarkan orangnya melihat.
      onChange({ fa: f, wo: daftar.length === 1 ? daftar[0] : null });
    } catch (e) {
      setWoList([]); setGalat(apiErrorMessage(e));
    } finally {
      setWoSibuk(false);
    }
  };

  const bersihkan = () => {
    onChange(null); setWoList([]); setKata(""); setHasil(null); setGalat("");
    setTimeout(() => kotak.current?.focus(), 0);
  };

  if (fa) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 space-y-2" data-testid={`${testId}-terpilih`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold text-foreground" data-testid={`${testId}-fa-number`}>{fa.fa_number}</span>
              <FAStatusBadge value={fa.order_status} />
            </div>
            {/* K206 / VONIS-HAK-BACA-ISI-FA: peran tanpa hak isi FA menerima nomor + status + tenggat saja. */}
            {"customer_name" in fa && (
              <div className="text-xs text-muted-foreground truncate">
                {fa.customer_name || "—"}{fa.customer_city ? ` (${fa.customer_city})` : ""}
                {fa.nama_team ? ` · ${fa.nama_team}` : ""}
              </div>
            )}
          </div>
          {!disabled && (
            <button type="button" onClick={bersihkan} aria-label="Ganti FA"
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    data-testid={`${testId}-ganti`}>
              <X size={16} />
            </button>
          )}
        </div>

        {/* WO: diturunkan, dan tiap keadaan bicara */}
        {!denganWo ? null : woSibuk ? (
          <div className="text-xs text-muted-foreground">Mencari WO…</div>
        ) : woList.length === 0 ? (
          <div className="text-xs text-warning-foreground bg-warning/20 rounded px-2 py-1" data-testid={`${testId}-wo-nol`}>
            FA ini belum punya Work Order. Buat WO dulu di menu Produksi → Work Order.
          </div>
        ) : woList.length === 1 ? (
          <div className="text-xs text-foreground" data-testid={`${testId}-wo-number`}>
            WO: <span className="font-mono font-medium">{woList[0].wo_number}</span>
            <span className="text-muted-foreground"> · {WO_STATUS_TONE[woList[0].status]?.l || "—"}</span>
          </div>
        ) : (
          <label className="block text-xs">
            <span className="text-muted-foreground">FA ini ada di {woList.length} WO — pilih satu:</span>
            <select className="mt-1 w-full rounded-lg border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                    value={wo?.id || ""} disabled={disabled}
                    onChange={(e) => onChange({ fa, wo: woList.find((w) => w.id === e.target.value) || null })}
                    data-testid={`${testId}-wo-pilih`}>
              <option value="">— pilih WO —</option>
              {woList.map((w) => (
                <option key={w.id} value={w.id}>{w.wo_number} · {WO_STATUS_TONE[w.status]?.l || "—"}</option>
              ))}
            </select>
          </label>
        )}
        {galat && <div className="text-xs text-destructive">{galat}</div>}
      </div>
    );
  }

  return (
    <div className="relative" data-testid={testId}>
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input ref={kotak} value={kata} onChange={(e) => setKata(e.target.value)} disabled={disabled}
             placeholder="Ketik nomor FA atau nama customer…" className="pl-8"
             aria-label="Cari FA" data-testid={`${testId}-cari`} />
      {(hasil !== null || sibuk || galat) && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-border bg-popover shadow-md text-sm"
             data-testid={`${testId}-hasil`}>
          {sibuk && <div className="px-3 py-2 text-muted-foreground">Mencari…</div>}
          {galat && <div className="px-3 py-2 text-destructive">{galat}</div>}
          {!sibuk && hasil && hasil.length === 0 && !galat && (
            <div className="px-3 py-2 text-muted-foreground">Tidak ada FA yang cocok dengan “{kata.trim()}”.</div>
          )}
          {!sibuk && hasil && hasil.map((f) => (
            <button type="button" key={f.id} onClick={() => pilihFa(f)}
                    className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between gap-2"
                    data-testid={`${testId}-opsi-${f.fa_number}`}>
              <span className="min-w-0">
                <span className="font-mono font-medium">{f.fa_number}</span>
                {"customer_name" in f && (
                  <span className="text-muted-foreground"> · {f.customer_name || "—"}{f.nama_team ? ` · ${f.nama_team}` : ""}</span>
                )}
              </span>
              <FAStatusBadge value={f.order_status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
